import type { LabObservation, LabOrder, LabResult } from "@/types/labs";

const flaggedCodes = new Set([
  "H",
  "HIGH",
  "L",
  "LOW",
  "A",
  "ABNORMAL",
  "HH",
  "LL",
  "CRITICAL",
  "CRITICAL HIGH",
  "CRITICAL LOW",
  "ABOVE HIGH NORMAL",
  "BELOW LOW NORMAL",
]);
const normalCodes = new Set([
  "N",
  "NORMAL",
  "IN RANGE",
  "WITHIN RANGE",
  "WITHIN NORMAL LIMITS",
]);
export function labFlag(
  item: LabObservation,
): "flagged" | "normal" | "unspecified" {
  const flags = item.interpretation
    .map((flag) => flag.trim().toUpperCase())
    .filter(Boolean);
  if (flags.some((flag) => flaggedCodes.has(flag))) return "flagged";
  if (flags.length && flags.every((flag) => normalCodes.has(flag)))
    return "normal";
  return "unspecified";
}
export function labFlagLabel(item: LabObservation) {
  const flags = item.interpretation.map((flag) => flag.trim().toUpperCase());
  if (
    flags.some((flag) =>
      ["HH", "LL", "CRITICAL", "CRITICAL HIGH", "CRITICAL LOW"].includes(flag),
    )
  )
    return "Critical · lab flag";
  if (flags.some((flag) => ["H", "HIGH", "ABOVE HIGH NORMAL"].includes(flag)))
    return "High";
  if (flags.some((flag) => ["L", "LOW", "BELOW LOW NORMAL"].includes(flag)))
    return "Low";
  return labFlag(item) === "flagged"
    ? "Flagged by lab"
    : labFlag(item) === "normal"
      ? "Normal (lab)"
      : "No lab flag supplied";
}
export const labCategories = [
  {
    id: "heart",
    name: "Heart health",
    description: "Cholesterol, lipoproteins and related measurements.",
    match:
      /cholesterol|lipoprotein|apolipoprotein|triglyceride|\bhdl\b|\bldl\b|\bvldl\b/i,
  },
  {
    id: "sugar",
    name: "Blood sugar",
    description: "Blood sugar measurements reported in this test.",
    match: /a1c|hba1c|glucose|insulin/i,
  },
  {
    id: "inflammation",
    name: "Inflammation",
    description: "Inflammation-related measurements in your report.",
    match: /crp|c-reactive|sedimentation/i,
  },
  {
    id: "hormones",
    name: "Hormones",
    description: "Hormone measurements reported by the laboratory.",
    match:
      /testosterone|estradiol|estrogen|progesterone|dhea|cortisol|\bfsh\b|\blh\b/i,
  },
  {
    id: "thyroid",
    name: "Thyroid",
    description: "Thyroid-related measurements in this test.",
    match: /thyroid|thyroxine|\btsh\b|\bt[34]\b/i,
  },
  {
    id: "kidney",
    name: "Kidney health",
    description: "Kidney-related measurements in your report.",
    match: /creatinine|egfr|urea|\bbun\b/i,
  },
  {
    id: "liver",
    name: "Liver & enzymes",
    description: "Liver-related values and enzyme measurements.",
    match: /alkaline phosphatase|bilirubin|albumin|\balt\b|\bast\b|\bggt\b/i,
  },
  {
    id: "other",
    name: "Other measurements",
    description: "Additional measurements supplied by the laboratory.",
    match: /.*/,
  },
];
export function labCategory(item: LabObservation) {
  return labCategories.find((category) => category.match.test(item.name))!;
}
export function labCounts(items: LabObservation[]) {
  const normal = items.filter((item) => labFlag(item) === "normal").length;
  const flagged = items.filter((item) => labFlag(item) === "flagged").length;
  return {
    total: items.length,
    normal,
    flagged,
    unspecified: items.length - normal - flagged,
  };
}
export type LabRecord = { order: LabOrder; result: LabResult };
export function labRecords(orders: LabOrder[]): LabRecord[] {
  return orders
    .flatMap((order) => order.results.map((result) => ({ order, result })))
    .sort(
      (a, b) =>
        Date.parse(b.result.created_at) - Date.parse(a.result.created_at),
    );
}
/** Only exact numeric values are charted; inequalities, qualitative values and units stay intact. */
export function numericMeasurement(item: LabObservation) {
  if (["entered-in-error", "cancelled"].includes(item.status ?? ""))
    return null;
  const match = item.value_text
    ?.trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*([^\d\n]*)$/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? { value, unit: match[2].trim() } : null;
}
export function markerIdentity(item: LabObservation) {
  return item.code && item.code_system
    ? `${item.code_system}:${item.code}`
    : `name:${item.name.trim().toLowerCase()}`;
}
export type MarkerPoint = {
  item: LabObservation;
  result: LabResult;
  date: string;
  time: number;
  value: number;
  unit: string;
};
/** Same code (or exact name), exact units, actual collection dates. Report revisions never create a trend. */
export function markerHistory(
  item: LabObservation,
  current: LabResult,
  records: LabRecord[],
): MarkerPoint[] {
  const numeric = numericMeasurement(item);
  if (!numeric) return [];
  const byCollection = new Map<string, MarkerPoint>();
  const results = [current, ...records.map((record) => record.result)].sort(
    (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
  );
  for (const result of results) {
    if (Date.parse(result.created_at) > Date.parse(current.created_at))
      continue;
    const matches = result.observations.filter(
      (other) => markerIdentity(other) === markerIdentity(item),
    );
    // Multiple observations of the same marker in one report need specimen context; don't guess.
    if (matches.length !== 1) continue;
    const other = matches[0];
    const parsed = numericMeasurement(other);
    const date = other.collected_at || result.collected_at;
    if (!date || !parsed || parsed.unit !== numeric.unit) continue;
    const time = Date.parse(date);
    if (!Number.isFinite(time)) continue;
    const collection = date.slice(0, 10);
    if (!byCollection.has(collection))
      byCollection.set(collection, {
        item: other,
        result,
        date,
        time,
        ...parsed,
      });
  }
  return [...byCollection.values()].sort((a, b) => a.time - b.time);
}
